import { emptyStateGenericGraphic } from '@workday/canvas-graphics-web';
import { Graphic } from '@workday/canvas-kit-react/icon';
import { Flex, FlexProps } from '@workday/canvas-kit-react/layout';
import { BodyText, Subtext } from '@workday/canvas-kit-react/text';
import { createStyles, px2rem } from '@workday/canvas-kit-styling';
import { system } from '@workday/canvas-tokens-web';
import { CanvasGraphic } from '@workday/design-assets-types';
import * as React from 'react';

/** Exported so callers can target these elements for layout overrides without duplicating the class name string. */
export const EMPTY_STATE_GRAPHIC_CLASS = 'empty__graphic';
export const EMPTY_STATE_TITLE_CLASS = 'empty__title';
export const EMPTY_STATE_DESCRIPTION_CLASS = 'empty__description';

export interface EmptyProps extends FlexProps {
  /** should always be title-case */
  title?: string;
  /** should always be complete sentences. react elements allowed for simple formatting of text (strong, etc.) */
  description?: string | React.ReactElement;
  graphicOverride?: CanvasGraphic;
  className?: string;
}

// By spreading the props, we're able to pass additional style/Canvas Kit theme props to the EmptyWrapper component
const Empty: React.FC<React.PropsWithChildren<EmptyProps>> = ({
  children,
  className,
  description,
  graphicOverride,
  title,
  ...props
}) => (
  <Flex
    data-testid='empty'
    data-cy='empty'
    cs={emptyWrapperStyles}
    className={`empty ${className ? className : ''}`}
    {...props}
  >
    <div className='empty__container'>
      <Graphic grow src={graphicOverride || emptyStateGenericGraphic} className={EMPTY_STATE_GRAPHIC_CLASS} />
      {title && (
        <BodyText size='medium' as='h4' className={`${EMPTY_STATE_TITLE_CLASS} ${titleStyles(!!description)}`}>
          {title}
        </BodyText>
      )}
      {description && (
        <Subtext size='large' className={EMPTY_STATE_DESCRIPTION_CLASS}>
          {description}
        </Subtext>
      )}
      {children}
    </div>
  </Flex>
);

const titleStyles = (hasDescription: boolean) =>
  createStyles({
    fontWeight: system.fontWeight.bold,
    margin: `0 0 ${hasDescription ? `var(${system.space.x2})` : `var(${system.space.x8})`} 0`,
  });

const emptyWrapperStyles = createStyles({
  minHeight: 'inherit',
  textAlign: 'center',
  height: '100%',
  '.empty': {
    '&__container': {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      margin: 'auto',
    },
    '&__graphic': {
      marginBottom: system.space.x8,
    },
    '&__description': {
      marginBottom: system.space.x8,
      marginTop: 0,
      maxWidth: px2rem(360),
    },
  },
});

export default Empty;
